import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import Groups from "./Components/Groups";
import Group from "./Components/Groups/Group";
import AddGroup from "./Components/Groups/Group/AddGroup";
import Billing from "./Components/Admin/Billing";
import User from "./Components/Admin/User";
import Courses from "./Components/Courses";
import CourseBoard from "./Components/Courses/CourseBoard";
import CourseStep from "./Components/Courses/CourseStep";
import ContentLibrary from "./Components/Content-Library";
import Chat from "./Components/Chat";
import DripCampaign from "./Components/Drip-Campaign";
import CampaignCards from "./Components/Drip-Campaign/CampaignCards";
import MyCalendar from "./Components/Calendar";
import Login from "./Components/Login/Login";
import ResetPassword from "./Components/ResetPassword";
import Signup from "./Components/Signup";
import Settings from "./Components/Settings";
import ProtectedRoute from "./route/protectedRoute";
import VerifyOTP from "./Components/VerifyOTP";
import { Provider } from "react-redux";
import store from "./store/index";
import useScreenWidth from "./utils/useScreenWidth";
import ScreenMessage from "./Components/ScreenMessage/ScreenMessage";
import EditGroup from "./Components/Groups/Group/EditGroup";
import DriversMotivation from "./Components/Onboarding/DriversMotivation";
import PlanForSuccess from "./Components/Onboarding/PlanForSuccess";
import Credo from "./Components/Onboarding/Credo";
import Ignition from "./Components/Onboarding/Ignition";
import GapMap from "./Components/Onboarding/GapMap";
import Next90 from "./Components/Onboarding/Next90";
import Next30 from "./Components/Onboarding/Next30";
import Key4 from "./Components/Onboarding/Key4";
import MessagePage from "./Components/Onboarding/MessagePage";
import WelcomePage from "./Components/Onboarding/WelcomePage";
import GetUserId from "./utils/getUserId";
import DailyWork from "./Components/DailyWork";
import Journal from "./Components/Journal/Journal";

const ScreenWrapper = ({ children }) => {
  const screenWidth = useScreenWidth();
  const threshold = 1024;

  if (screenWidth < threshold) {
    return <ScreenMessage />;
  }

  return children;
};

const router = createBrowserRouter([
  {
    path: "login",
    element: (
      <ScreenWrapper>
        <Login />
      </ScreenWrapper>
    ),
  },
  {
    path: "reset-password",
    element: (
      <ScreenWrapper>
        <ResetPassword />
      </ScreenWrapper>
    ),
  },
  {
    path: "signup",
    element: (
      <ScreenWrapper>
        <Signup />
      </ScreenWrapper>
    ),
  },
  {
    path: "verify-otp",
    element: (
      <ScreenWrapper>
        <VerifyOTP />
      </ScreenWrapper>
    ),
  },
  {
    path: "message-page",
    element: (
      <GetUserId>
        <MessagePage />
      </GetUserId>
    ),
  },
  {
    path: "welcome-to-stg",
    element: (
      <GetUserId>
        <WelcomePage />
      </GetUserId>
    ),
  },
  {
    path: "onboarding-driver",
    element: (
      <GetUserId>
        <Outlet />
      </GetUserId>
    ),
    children: [
      {
        index: true,
        element: <DriversMotivation />,
      },
      {
        path: "credo",
        element: <Outlet />,
        children: [
          {
            index: true,
            element: <Credo />,
          },
          {
            path: "edit",
            element: <Credo />,
          },
        ],
      },
      {
        path: "ignition",
        element: <Outlet />,
        children: [
          {
            index: true,
            element: <Ignition />,
          },
          {
            path: "edit",
            element: <Ignition />,
          },
        ],
      },
      {
        path: "gap-map",
        element: <GapMap />,
      },
    ],
  },
  {
    path: "plan-for-success",
    element: (
      <GetUserId>
        <Outlet />
      </GetUserId>
    ),
    children: [
      {
        index: true,
        element: <PlanForSuccess />,
      },
      {
        path: "key4",
        element: <Key4 />,
      },
      {
        path: "next90",
        element: <Outlet />,
        children: [
          {
            index: true,
            element: <Next90 />,
          },
          {
            path: "edit",
            element: <Next90 />,
          },
        ],
      },
      {
        path: "next30",
        element: <Outlet />,
        children: [
          {
            index: true,
            element: <Next30 />,
          },
          {
            path: "edit",
            element: <Next30 />,
          },
        ],
      },
    ],
  },
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <App />
      </ProtectedRoute>
    ),
    children: [
      {
        path: "daily-work",
        element: (
          <ProtectedRoute>
            <DailyWork />
          </ProtectedRoute>
        ),
      },
      {
        path: "groups",
        element: (
          <ProtectedRoute>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Groups />,
          },
          {
            path: "group",
            element: (
              <ProtectedRoute>
                <Outlet />
              </ProtectedRoute>
            ),
            children: [
              {
                index: true, // This handles "/group" by rendering Groups or redirecting
                element: <Navigate to="/groups" replace />, // Or another component as needed
              },
              {
                path: ":id",
                element: <Group />,
              },
              {
                path: "add",
                element: <AddGroup />,
              },
              {
                path: "edit",
                element: <Outlet />,
                children: [
                  {
                    path: ":id",
                    element: <EditGroup />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "billing",
        element: (
          <ProtectedRoute>
            <Billing />
          </ProtectedRoute>
        ),
      },
      {
        path: "users",
        element: (
          <ProtectedRoute>
            <User />
          </ProtectedRoute>
        ),
      },
      {
        path: "courses",
        element: (
          <ProtectedRoute>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Courses />,
          },
          {
            path: "course/:index/:title",
            element: <CourseBoard />,
          },
          {
            path: "step/:title",
            element: <CourseStep />,
          },
        ],
      },
      {
        path: "content-library",
        element: (
          <ProtectedRoute>
            <ContentLibrary />
          </ProtectedRoute>
        ),
      },
      {
        path: "chat",
        element: (
          <ProtectedRoute>
            <Chat />
          </ProtectedRoute>
        ),
      },
      {
        path: "campaigns",
        element: (
          <ProtectedRoute>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <DripCampaign />,
          },
          {
            path: ":id",
            element: <CampaignCards />,
          },
        ],
      },
      {
        path: "calendar",
        element: (
          <ProtectedRoute>
            <MyCalendar />
          </ProtectedRoute>
        ),
      },
      {
        path: "settings",
        element: (
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        ),
      },
      {
        path: "journal",
        element: (
          <ProtectedRoute>
            <Journal />
          </ProtectedRoute>
        ),
      },
    ],
  },
]);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <Provider store={store}>
    <RouterProvider router={router} />
  </Provider>
);
